import { useCallback, useMemo, useState } from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { useToast } from '@winnin/winnin_ui.components.toast'

import { useDashboardModals } from 'contexts/DashboardModalsContext'
import { useDebouncedValue } from 'hooks/use-debounced-value'
import { useDashboardMutations, useDashboardsQueries } from 'hooks-queries/dashboards'
import { useTranslate } from 'lib/tolgee'
import { analyticsTracker, TOLGEE_GLOBALS } from 'utils'

import {
  ADD_DASHBOARD_TO_FOLDER_LOG,
  BASE_PAGE_SIZE,
  LOAD_DASHBOARD_FOLDERS_LOG,
} from './select-dashboard-folder-modal.consts'
import { translations } from './select-dashboard-folder-modal.i18n'
import type { ISelectDashboardFolderModal, TFolder } from './select-dashboard-folder-modal.types'

export const useSelectDashboardFolderModalModel = (): ISelectDashboardFolderModal => {
  const [selectedFolder, setSelectedFolder] = useState<TFolder>(null)
  const { debouncedValue, visualValue, handleAction } = useDebouncedValue({ initialValue: '' })

  const { setIsSelectDashboardFolderModalOpen, dashboard } = useDashboardModals()
  const { createToast } = useToast()
  const t = useTranslate()

  const { data, fetchMore, loading, error } = useDashboardsQueries({
    queryName: 'getJobExternalFolders',
    errorLog: LOAD_DASHBOARD_FOLDERS_LOG,
    queryVariables: {
      pagination: {
        cursor: null,
        take: BASE_PAGE_SIZE,
      },
      name: debouncedValue,
    },
    options: {
      fetchPolicy: 'no-cache',
    },
  })

  const [createWorkspaceJobFolderRelations, { loading: mutationLoading }] = useDashboardMutations({
    mutationName: 'createWorkspaceJobFolderRelations',
    errorLog: ADD_DASHBOARD_TO_FOLDER_LOG,
    options: {
      refetchQueries: ['getListWorkspaceJobPaginated', 'GET_JOB_EXTERNAL_FOLDERS', 'getWorkspaceJobExternalFolder'],
    },
  })

  const foldersList = useMemo(() => {
    return (
      data?.listWorkspaceJobExternalFolders?.data?.map(item => {
        return {
          folderName: item.name,
          dashboardCount: item.jobsCount,
          id: item.id,
        }
      }) || []
    )
  }, [data?.listWorkspaceJobExternalFolders?.data])

  const handleInputChange: React.ChangeEventHandler<HTMLInputElement> &
    React.ChangeEventHandler<HTMLTextAreaElement> = e => {
    const value = e.target.value
    handleAction(value)
  }

  const handleSelectFolder = (folder: TFolder) => {
    setSelectedFolder(folder)
  }

  const handleClose = useCallback(() => {
    setIsSelectDashboardFolderModalOpen(false)
  }, [setIsSelectDashboardFolderModalOpen])

  const handleSubmit = useCallback(() => {
    if (!selectedFolder || !dashboard) {
      createToast({
        content: t(TOLGEE_GLOBALS.somethingWentWrong),
      })
      handleClose()
      return
    }

    const { DASHBOARD_FOLDERS } = analyticsTracker.eventNames
    analyticsTracker.eventTrack(DASHBOARD_FOLDERS.ADDED_TO_FOLDER, {
      folderId: selectedFolder.id,
      folderName: selectedFolder.folderName,
      dashboardId: dashboard.id,
      dashboardName: dashboard.name,
    })

    createWorkspaceJobFolderRelations({
      variables: {
        input: {
          folderId: selectedFolder.id,
          workspaceJobIds: [dashboard.id],
        },
      },
    })
      .then(({ data }) => {
        if (data.createWorkspaceJobFolderRelations.success) {
          createToast({
            content: t(translations.addedToFolder),
          })
        }
      })
      .catch(() => {
        createToast({
          content: t(TOLGEE_GLOBALS.somethingWentWrong),
        })
      })
      .finally(() => {
        handleClose()
      })
  }, [createToast, createWorkspaceJobFolderRelations, dashboard, handleClose, selectedFolder, t])

  const isSubmitDisabled = !selectedFolder || mutationLoading

  const hasMore = useMemo(() => {
    return Boolean(data?.listWorkspaceJobExternalFolders?.cursor)
  }, [data?.listWorkspaceJobExternalFolders?.cursor])

  const loadMore = useCallback(async () => {
    const cursor = data?.listWorkspaceJobExternalFolders?.cursor
    if (loading || !hasMore) return
    try {
      await fetchMore({
        variables: {
          pagination: {
            take: BASE_PAGE_SIZE,
            cursor,
          },
          name: debouncedValue,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult.listWorkspaceJobExternalFolders) {
            return prev
          }

          return Object.assign({}, prev, {
            listWorkspaceJobExternalFolders: {
              ...fetchMoreResult.listWorkspaceJobExternalFolders,
              data: [
                ...(prev.listWorkspaceJobExternalFolders?.data || []),
                ...(fetchMoreResult?.listWorkspaceJobExternalFolders?.data || []),
              ],
            },
          })
        },
      })
    } catch {
      // Errors are reported centrally by the Apollo ErrorLink.
    }
  }, [data?.listWorkspaceJobExternalFolders?.cursor, debouncedValue, fetchMore, hasMore, loading])

  const isEmpty = !foldersList.length && !loading

  const [sentryRef] = useInfiniteScroll({
    loading: loading,
    hasNextPage: hasMore,
    onLoadMore: loadMore,
    disabled: !!error,
  })

  return {
    handleClose,
    isSubmitDisabled,
    foldersList,
    handleSelectFolder,
    selectedFolder,
    sentryRef,
    hasMore,
    loading: mutationLoading || loading,
    handleSubmit,
    handleInputChange,
    inputValue: visualValue,
    isEmpty,
  }
}
